import type { GameCode } from '../../entities/room/types';
import { SICBO_SUM_ODDS } from './betOptions.ts';

/**
 * What a round can land on, from a bet's point of view.
 *
 * The same shape the danmaku ticker feeds its evaluator, because it is also the
 * shape of a *hypothetical* draw — which is what lets the pre-confirm preview
 * ask "what if the wheel lands on 17?" without a real round.
 */
export type DrawOutcome = {
  lastdigit?: number;
  sicboDice?: number[];
  sicboSum?: number;
  sicboTriple: number | null;
  rouletteNum?: number;
  rouletteColor?: 'red' | 'black' | 'green';
};

/** The shape the preview needs off a bet: where it sits and what it costs. */
export type WinPreviewBet = {
  tab?: string;
  pick: string | number | undefined;
  amount: number;
};

export function rouletteColor(value: number): 'red' | 'black' | 'green' {
  if (value === 0) return 'green';
  return ROULETTE_REDS.has(value) ? 'red' : 'black';
}

const ROULETTE_REDS = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);

export function rouletteSplitNumbers(pick: string) {
  const [, a, b] = pick.split('_');
  return [Number(a), Number(b)].filter((item) => Number.isFinite(item));
}

export function rouletteStreetNumbers(index: number) {
  if (!Number.isFinite(index) || index < 1 || index > 12) return [];
  const start = (index - 1) * 3 + 1;
  return [start, start + 1, start + 2];
}

export function rouletteCornerNumbers(topLeft: number) {
  if (!Number.isFinite(topLeft) || topLeft < 1 || topLeft > 32 || topLeft % 3 === 0) return [];
  return [topLeft, topLeft + 1, topLeft + 3, topLeft + 4];
}

export function rouletteLineNumbers(index: number) {
  if (!Number.isFinite(index) || index < 1 || index > 11) return [];
  const start = (index - 1) * 3 + 1;
  return [start, start + 1, start + 2, start + 3, start + 4, start + 5];
}

/**
 * The headline multiplier for a tab/pick, for tabs that have exactly one.
 *
 * Sum reads the same table the grid prints on its tiles; a second hand-written
 * copy here had drifted one notch low on almost every row, so the sheet quoted
 * a win that contradicted the ×62 the player had just tapped.
 *
 * Sicbo Single is the odd one out: it pays ×2/×3/×6 by how many dice match, so
 * the number here is its ceiling. Nothing should read a *batch* total off this
 * function — use `batchWinRange`, which asks the outcomes instead.
 */
export function defaultOddsForBet(game: GameCode, tab: string | undefined, pick: string) {
  if (game === 'lastdigit') {
    return tab === 'specific' ? 9.5 : 2; // exact digit ×9.5; odd/even & big/small ×2
  }
  if (game === 'sicbo') {
    if (tab === 'sicbo-sum') return SICBO_SUM_ODDS[Number(String(pick).replace(/^s/, ''))] ?? 0;
    if (tab === 'sicbo-bs') return 1.95;
    if (tab === 'sicbo-single') return 6;   // top payout: all 3 dice match (×2/×3/×6)
    if (tab === 'sicbo-combo') return 6.5;  // both numbers appear (panel: ×6.5)
    if (tab === 'sicbo-pairs') {
      if (String(pick).startsWith('t')) return 180;
      if (String(pick).startsWith('d')) return 12;
      if (pick === 'any') return 30;
      if (pick === 'ad') return 2.2;
      return 12;
    }
    return 2;
  }
  if (game === 'roulette') {
    if (tab === 'roulette-num') return 35;
    if (tab === 'roulette-dozen' || tab === 'roulette-column') return 3;
    if (tab === 'roulette-split') return 17;
    if (tab === 'roulette-street') return 11;
    if (tab === 'roulette-corner') return 8;
    if (tab === 'roulette-line') return 6;
    return 1.95; // red/black, odd/even, hi/lo
  }
  return 1.95;
}

/**
 * Settle one bet against one draw — real or hypothetical.
 *
 * `odds` is the actual multiplier for THIS draw, not the headline: Last-Digit's
 * boundary digits pay ×1.5 and a Sicbo Single pays by match count, so a bet's
 * return is a property of the outcome, never of the pick alone.
 */
export function evalBetAgainstDraw(
  game: GameCode,
  tab: string | undefined,
  rawPick: string | number | undefined,
  draw: DrawOutcome,
) {
  const pickText = String(rawPick ?? '');
  if (game === 'lastdigit') {
    const digit = draw.lastdigit;
    if (digit == null) return { won: false, odds: 0 };
    if (pickText === 'odd') return { won: digit % 2 === 1, odds: digit === 9 ? 1.5 : 2 };
    if (pickText === 'even') return { won: digit % 2 === 0, odds: digit === 0 ? 1.5 : 2 };
    if (pickText === 'big') return { won: digit >= 5, odds: digit === 9 ? 1.5 : 2 };
    if (pickText === 'small') return { won: digit < 5, odds: digit === 0 ? 1.5 : 2 };
    const specific = Number(pickText);
    return { won: Number.isFinite(specific) && specific === digit, odds: 9.5 };
  }

  if (game === 'sicbo') {
    const dice = draw.sicboDice || [];
    const sum = Number(draw.sicboSum || 0);
    const isTriple = draw.sicboTriple !== null;
    if (dice.length !== 3) return { won: false, odds: 0 };
    if (tab === 'sicbo-bs') {
      if (isTriple) return { won: false, odds: 1.95 };
      if (pickText === 'big') return { won: sum >= 11 && sum <= 17, odds: 1.95 };
      if (pickText === 'small') return { won: sum >= 4 && sum <= 10, odds: 1.95 };
    }
    if (tab === 'sicbo-sum' && pickText.startsWith('s')) {
      const target = Number(pickText.slice(1));
      return { won: sum === target, odds: defaultOddsForBet(game, tab, pickText) };
    }
    if (tab === 'sicbo-single') {
      const target = Number(pickText);
      const count = dice.filter((item) => item === target).length;
      if (count === 0) return { won: false, odds: 0 };
      return { won: true, odds: count === 3 ? 6 : count === 2 ? 3 : 2 };
    }
    if (tab === 'sicbo-combo' && pickText.startsWith('c')) {
      const a = Number(pickText[1]);
      const b = Number(pickText[2]);
      return { won: dice.includes(a) && dice.includes(b), odds: 6.5 };
    }
    if (tab === 'sicbo-pairs' || tab === 'sicbo-double' || tab === 'sicbo-triple') {
      if (pickText === 'any') return { won: isTriple, odds: 30 };
      if (pickText === 'ad') {
        if (isTriple) return { won: false, odds: 0 };
        const counts = [0, 0, 0, 0, 0, 0, 0];
        for (const die of dice) counts[die] += 1;
        return { won: counts.some((count) => count === 2), odds: 2.2 };
      }
      if (pickText.startsWith('d')) {
        const target = Number(pickText.slice(1));
        return { won: dice.filter((item) => item === target).length >= 2, odds: 12 };
      }
      if (pickText.startsWith('t')) {
        const target = Number(pickText.slice(1));
        return { won: draw.sicboTriple === target, odds: 180 };
      }
    }
  }

  if (game === 'roulette') {
    const value = draw.rouletteNum;
    if (value == null) return { won: false, odds: 0 };
    if (pickText === 'red') return { won: draw.rouletteColor === 'red', odds: 1.95 };
    if (pickText === 'black') return { won: draw.rouletteColor === 'black', odds: 1.95 };
    if (pickText === 'odd') return { won: value !== 0 && value % 2 === 1, odds: 1.95 };
    if (pickText === 'even') return { won: value !== 0 && value % 2 === 0, odds: 1.95 };
    if (pickText === 'hi') return { won: value >= 19 && value <= 36, odds: 1.95 };
    if (pickText === 'lo') return { won: value >= 1 && value <= 18, odds: 1.95 };
    if (pickText === 'dz1') return { won: value >= 1 && value <= 12, odds: 3 };
    if (pickText === 'dz2') return { won: value >= 13 && value <= 24, odds: 3 };
    if (pickText === 'dz3') return { won: value >= 25 && value <= 36, odds: 3 };
    if (pickText === 'col1') return { won: value >= 1 && value % 3 === 1, odds: 3 };
    if (pickText === 'col2') return { won: value >= 1 && value % 3 === 2, odds: 3 };
    if (pickText === 'col3') return { won: value >= 1 && value % 3 === 0, odds: 3 };
    if (pickText.startsWith('sp_')) return { won: rouletteSplitNumbers(pickText).includes(value), odds: 17 };
    if (pickText.startsWith('st')) return { won: rouletteStreetNumbers(Number(pickText.slice(2))).includes(value), odds: 11 };
    if (pickText.startsWith('cn')) return { won: rouletteCornerNumbers(Number(pickText.slice(2))).includes(value), odds: 8 };
    if (pickText.startsWith('ln')) return { won: rouletteLineNumbers(Number(pickText.slice(2))).includes(value), odds: 6 };
    const specific = Number(pickText);
    if (pickText !== '' && Number.isFinite(specific)) return { won: specific === value, odds: 35 };
  }

  return { won: false, odds: 0 };
}

/**
 * Every draw a round of this game can produce. Small enough to walk in full:
 * 10 digits, 37 pockets, 216 ordered dice triples.
 */
function buildOutcomeSpace(game: GameCode): DrawOutcome[] {
  if (game === 'lastdigit') {
    return Array.from({ length: 10 }, (_, digit) => ({ lastdigit: digit, sicboTriple: null }));
  }
  if (game === 'roulette') {
    return Array.from({ length: 37 }, (_, num) => ({
      rouletteNum: num,
      rouletteColor: rouletteColor(num),
      sicboTriple: null,
    }));
  }
  const outcomes: DrawOutcome[] = [];
  for (let a = 1; a <= 6; a += 1) {
    for (let b = 1; b <= 6; b += 1) {
      for (let c = 1; c <= 6; c += 1) {
        outcomes.push({
          sicboDice: [a, b, c],
          sicboSum: a + b + c,
          sicboTriple: a === b && b === c ? a : null,
        });
      }
    }
  }
  return outcomes;
}

const outcomeSpaceCache = new Map<GameCode, DrawOutcome[]>();

export function outcomeSpace(game: GameCode): DrawOutcome[] {
  const cached = outcomeSpaceCache.get(game);
  if (cached) return cached;
  const built = buildOutcomeSpace(game);
  outcomeSpaceCache.set(game, built);
  return built;
}

/**
 * What a whole batch can return, given that the round produces ONE draw.
 *
 * Summing each bet's own best case — which is what the preview did — quotes a
 * total no draw can pay: back all 37 roulette pockets at R$1 and it promised
 * +R$1.295 when the truth is +R$35, because 36 of those bets have to lose for
 * the 37th to win. So walk the outcomes and settle the whole batch against
 * each one; the best and worst *winning* draw are then facts, not sums.
 *
 * `min` is the smallest total a player still walks away from with something,
 * so it ignores draws that pay nothing (0 only when nothing can ever win).
 * Both figures are gross returns — stake included — same as `payout` on a
 * settled bet.
 */
export function batchWinRange(bets: WinPreviewBet[], game: GameCode): { max: number; min: number } {
  if (bets.length === 0) return { max: 0, min: 0 };
  let max = 0;
  let min = Infinity;
  for (const draw of outcomeSpace(game)) {
    let total = 0;
    for (const bet of bets) {
      const { won, odds } = evalBetAgainstDraw(game, bet.tab, bet.pick, draw);
      if (won) total += Number(bet.amount || 0) * odds;
    }
    if (total > max) max = total;
    if (total > 0 && total < min) min = total;
  }
  return { max, min: min === Infinity ? 0 : min };
}
